import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import userStore from "../store/userStore";
import apiErrorStore from "../store/apiErrorStore";
import reportListStore from "../store/reportListStore";
import DropdownMenu from "./DropdownMenu";
import login from "../assets/login.svg";
import newLogo from "../assets/newLogo.svg";
import switchDashboard from "../assets/switchDashboard.svg";
import { ROUTE } from "../constants";
import useReportStore from "../store/reportStore";
import { useCIAMAuth } from "@ciam/ciam-kit";
import { useMsal } from "@azure/msal-react";
import { sendLog } from "../utils/sendLog";
import modalStore from "../store/modalStore";
import { getCurrentMerchant } from "../utils/getCurrentMerchant";
import {
  USBIconHome,
  USBIconInfo,
  USBIconProfile,
} from "@usb-shield/react-icons";
import clearAppSession from "../utils/clearAppSession";

const useMsalSafe = () => {
  try {
    return useMsal();
  } catch {
    return { instance: null };
  }
};

const useCIAMAuthSafe = () => {
  try {
    return useCIAMAuth();
  } catch {
    return null;
  }
};

const Header = () => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const reportId = queryParams.get("id");
  const dropdownRef = useRef(null);

  const { openDisclosure } = modalStore();
  const { userName } = userStore();
  const { reportName, setReport, resetReport, setIsSwitchReport } =
    useReportStore();
  const { reportList } = reportListStore();
  const { errorInFetchingData } = apiErrorStore();

  const isHomePage = pathname === ROUTE.HOME;
  const isDashboardPage = pathname === ROUTE.DASHBOARD;
  const isHelpCenterPage = pathname === ROUTE.HELP_CENTER;
  const [openDropdown, setOpenDropdown] = useState(false);

  const ciamAuth = useCIAMAuthSafe();
  const { instance: msalInstance } = useMsalSafe();
  const currentMerchant = getCurrentMerchant();
  const loginType = sessionStorage.getItem("loginType");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
    };

    const handleWindowBlur = () => {
      setOpenDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const toggleSwitch = () => {
    setOpenDropdown((openDropdown) => !openDropdown);
  };

  const navigateToReport = (report) => {
    resetReport();
    setReport(report);
    setIsSwitchReport(true);
    sendLog("switch_report", {
      page: pathname,
      action: `Switch to report: ${report.name}`,
      reportName: report.name,
      merchantName: currentMerchant,
    });
    navigate(`${ROUTE.DASHBOARD}?id=${report.id}`);
  };

  const onLogout = () => {
    sendLog("button_click_logout", {
      page: pathname,
      action: "User logged out",
    });
    if (loginType === "sso" && msalInstance) {
      // Clear app-owned keys only. A blanket sessionStorage.clear() would wipe
      // the MSAL cache before logoutRedirect can read it.
      const account = msalInstance.getActiveAccount();
      clearAppSession();
      msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin,
      });
    } else if (ciamAuth) {
      ciamAuth.logOut();
    }
  };

  const navigateToHome = () => {
    sendLog("button_click_home", {
      page: pathname,
      action: `Navigate to Home page from ${pathname.slice(1)}`,
    });
    navigate(ROUTE.HOME);
  };

  const navigateToHelpCenter = () => {
    navigate(ROUTE.HELP_CENTER);
  };

  return (
    <div className="header d-flex justify-content-between align-center">
      <div className="appname pb-1">
        <img
          data-src={newLogo}
          alt="newLogo"
          width="240"
          height="25"
          ref={(img) => {
            if (img) img.src = newLogo;
          }}
        />
        <p className={isDashboardPage && "separator"}>
          <span>{isDashboardPage ? reportName : ""}</span>
        </p>
      </div>
      <div className="d-flex">
        <label
          className="faded-label ps-1 d-flex align-center pe-4"
          onClick={openDisclosure}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openDisclosure();
            }
          }}
          tabIndex={0}
          role="button"
        >
          Disclosure
        </label>

        {/* Language Dropdown for Landing Page (Home Page) */}
        {isHomePage && (
          <div className="d-flex align-center me-4">
            <DropdownMenu />
          </div>
        )}

        {/*  NEW: Help Center Icon - visible on both pages Home and Dashboard */}
        {!errorInFetchingData && !isHelpCenterPage && (
          <div
            onClick={navigateToHelpCenter}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                navigateToHelpCenter();
              }
            }}
            role="button"
            data-testid="help_center"
            className="home-navigation me-4"
          >
            <USBIconInfo size={20} />
            <label>Help Center</label>
          </div>
        )}

        {/* Home button and Switch Dashboard Icon on Dashboard Page only */}
        {(isDashboardPage || isHelpCenterPage) && (
          <div
            role="button"
            className="home-navigation me-4"
            onClick={() => navigateToHome()}
          >
            <USBIconHome size={20} />
            <label>Home</label>
          </div>
        )}

        {isDashboardPage && (
          <div
            role="button"
            data-testid="switch-dashboard"
            className="home-navigation me-4"
            onClick={() => toggleSwitch()}
          >
            <img
              data-src={switchDashboard}
              alt="switchDashboard"
              width="24"
              height="22"
              ref={(img) => {
                if (img) img.src = switchDashboard;
              }}
            />
            <label className="pt-2px">Switch Dashboard</label>
            {openDropdown && (
              <div className="dropdown-container" ref={dropdownRef}>
                <ul className="dropdown-menu">
                  {reportList.map((report) => (
                    <li
                      role="button"
                      key={report.id}
                      aria-disabled={
                        report.disabled || report.killSwitchEnabled
                      }
                      onClick={() =>
                        reportId !== report.id &&
                        !report.killSwitchEnabled &&
                        !report.disabled &&
                        navigateToReport(report)
                      }
                      className={[
                        "dropdown-item",
                        reportId === report.id ? "selected" : "",
                        report.killSwitchEnabled || report.disabled
                          ? "dropdown-item--disabled cursor-not-allowed"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <img
                        src={report.img}
                        className="list-icon"
                        alt="dashboard-icon"
                        width="28"
                        height="28"
                      />
                      {report.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Content that represents username and logout button on all screens accept pre-login screen */}
        {!errorInFetchingData && (
          <>
            <div className="home-navigation me-4">
              <USBIconProfile size={20} />
              <label>{userName}</label>
            </div>
            <div
              role="button"
              data-testid="logout"
              className="home-navigation me-4"
              onClick={() => onLogout()}
            >
              <img
                data-src={login}
                alt="logout"
                ref={(img) => {
                  if (img) img.src = login;
                }}
                width="24"
                height="22"
                title="Logout"
              />
              <label className="pt-2px">Logout</label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
